import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, Subscription, catchError, firstValueFrom, of, take } from 'rxjs';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

import { AlertService } from '@app/shared/alert.service';
import { AlertAction } from '@app/shared/_models/alert';
import { ValuesService } from '@app/common/values/values.service';
import { DelrunsService } from '@app/logistic/delruns/delruns.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { GeoService } from '@app/shared/geo.service';
import { TzDatePipe } from '@app/shared/tz-date.pipe';
import { MiniMapComponent } from '@app/shared/mini-map/mini-map.component';
import { ImageCarouselComponent } from '@app/shared/attach/image-carousel/image-carousel.component';
import { RentalDirectionComponent } from '@app/shared/rental-direction/rental-direction.component';
import { PartnerService } from '@app/common/partner/partner.service';
import { openPartnerSheet } from '@app/common/partner/partner-sheet';
import { C_value, Partner } from '@app/shared/_models/common';
import { Delivery, Sales_order, roundHalfAway } from '@app/shared/_models/sales';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { LogisticService } from '@app/logistic/logistic.service';
import { DeliveryComponent } from '@app/logistic/delivery/delivery.component';
import { DeliveryGeo } from '@app/logistic/delivery/delivery-geo';
import { DeliveryPrintService } from '@app/logistic/delivery/delivery-print.service';
import { DeliveryValidationComponent } from '@app/logistic/delivery/delivery-validation/delivery-validation.component';
import { PlanificationdeliveryComponent } from '@app/logistic/delivery/planificationdelivery/planificationdelivery.component';
import { serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import { normalizeSearch } from '@app/hr/hr-leave-board';
import * as constants from '@app/shared/constants';
import { SalesService } from '@app/sales/sales.service';
import { LoginService } from '@app/common/login.service';
import { SalesOrderState } from '@app/shared/constants-server';
import { periodAllowsSave } from '@app/sales/rental-contract/rental-period-gate';
import { prepareRentalSave } from '@app/sales/rental-contract/rental-contract.save';
import { CLOSED_LINE } from '@app/sales/rental-billing/rental-billing.labels';
import { ATTACH_FLAG_SCAN_BL, TourReturnService } from '@app/sales/tour-return/tour-return.service';
import { businessDayOf, todayStr } from '@app/shared/time';
import { askDate } from '@app/shared/confirm-date-dialog/confirm-date-dialog.component';
import { PassageSale, RentalDeliveryData, RentalDeliveryRow, blWhen, closedPose, deliveryLinesOf, isSigned, openDeliveries, passageSales, pickActive, renumberedLines, rowsOf, salePassageDelivery } from './rental-delivery.model';

// Fenêtre d'un passage chez le client (décisions de JP des 29 et 30/10/2026) : le BL du dossier (poses +, reprises −) et celui de chaque commande de vente liée,
// même planification, sans l'historique du dossier (lecture « pending »). Planification, carte et impression sont les blocs de la fenêtre F2.
@Component({
  selector: 'app-rental-delivery-dialog',
  imports: [NgTemplateOutlet, RouterLink, ReactiveFormsModule, MatButtonModule, MatIcon, MatTooltipModule, TzDatePipe, PlanificationdeliveryComponent, MiniMapComponent, ImageCarouselComponent, RentalDirectionComponent],
  templateUrl: './rental-delivery-dialog.component.html',
  styleUrls: ['../../logistic/delivery/delivery.component.scss', './rental-delivery-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class RentalDeliveryDialogComponent implements OnInit, OnDestroy {
  private data = inject<RentalDeliveryData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<RentalDeliveryDialogComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);
  private alertService = inject(AlertService);
  private spin = inject(SpinnerService);
  private quotationService = inject(QuotationService);
  private logisticService = inject(LogisticService);
  private partnerService = inject(PartnerService);
  private printer = inject(DeliveryPrintService);
  private router = inject(Router);
  private valuesService = inject(ValuesService);
  private salesService = inject(SalesService);
  private loginService = inject(LoginService);
  private tourReturnService = inject(TourReturnService);
  private deliverySlots: C_value[] = [];	// c_values 8 : rattache un horaire (10h…) à sa demi-journée pour le lien vers le planning ; nom du créneau des onglets
  geo = new DeliveryGeo(inject(GeoService), this.partnerService, this.spin, this.alertService);

  @HostBinding('class.in-dialog') readonly inDialog = true;	// hauteur de modale des règles partagées de la fenêtre F2

  order: Sales_order | null = null;
  active: Delivery | null = null;		// BL affiché ; null = BL à créer au premier enregistrement
  formGroup: FormGroup = new FormGroup({});
  rows: RentalDeliveryRow[] = [];		// sur ce BL ou à livrer, triées par rang
  elsewhere: RentalDeliveryRow[] = [];	// déjà sur un autre BL non signé : non sélectionnables
  legacy: RentalDeliveryRow[] = [];		// anciennes lignes RENTAL_RETURN de ce BL : jamais sélectionnables, retirées à l'enregistrement
  chosen = new Map<number, number>();	// lignes mises sur ce BL → quantité
  private savedOn = new Map<number, Delivery>();	// lignes déjà écrites sur un BL du passage : les décocher demande confirmation (JP 31/10)
  beneficiary: any = null;
  isSaving = false;
  changed = false;
  wrote = false;					// au moins un enregistrement : l'appelant relit la pièce (lu aussi à la fermeture par Échap)
  private requested: number[] = [];
  private collect: number[] = [];		// matériels posés à reprendre (Récupérer) : proposés à −1, cochés
  private saleOrderIds: number[] = [];	// commandes de vente à livrer dans ce passage : leurs lignes cochées
  sales: PassageSale[] = [];		// commandes de vente du passage : livrables, ou dont un BL ouvert le désigne (#30), avec ce BL
  salesLoading = false;			// #29 : ventes du passage pas encore relues, Imprimer / Signer / Planifier / Supprimer bloqués
  private dead = new Set<number>();	// commandes annulées dont le BL ouvert désigne le passage : retiré à l'enregistrement
  readonly patient = this.data.patient ?? null;	// camion d'un patient : ni les matériels des autres patients ni les ventes du dossier ne sont proposés
  private readonly patientKey = this.patient === null ? null : normalizeSearch(this.patient);
  loadOrderEventsSubject = new BehaviorSubject<FormGroup>(this.formGroup);
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(this.valuesService.getValues$(constants.C_VALUES_DELIVERY_SLOT).pipe(take(1))
      .subscribe({ next: (slots: C_value[]) => this.deliverySlots = slots ?? [], error: () => this.deliverySlots = [] }));	// sans eux : lien sur la journée entière
    this.requested = (this.data.lineIds ?? []).map(Number).filter((id) => id > 0);
    this.collect = (this.data.collectIds ?? []).map(Number).filter((id) => id > 0);
    this.saleOrderIds = (this.data.saleOrderIds ?? []).map(Number).filter((id) => id > 0);
    this.load(this.data.deliveryId);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.geo.destroy();
  }

  // Le dossier n'est jamais lu en entier : lignes encore à livrer ou sur un BL ouvert, et leurs seuls BL (o=pending du serveur).
  private load(deliveryId?: number, then?: () => void): void {
    const o = '?o=deliveries,attachs,pending' + (deliveryId ? ',bl:' + deliveryId : '') + this.collect.map((id) => ',collect:' + id).join('');
    this.salesLoading = true;
    this.subscriptions.add(this.quotationService.getQuotationById(this.data.id, o).pipe(this.spin.block('Chargement des livraisons…')).subscribe({
      next: (orders: any[]) => {
        if (!orders?.length) { this.salesLoading = false; this.alertService.error('Dossier de location introuvable'); return; }
        this.show(orders[0], deliveryId);
        this.loadSales(then);
      },
      error: (e) => { this.salesLoading = false; this.alertService.error('ERREUR au chargement des livraisons : ' + serverMessage(e)); },
    }));
  }

  private show(order: Sales_order, deliveryId?: number): void {
    const sameAddress = this.order?.addr_deliv === order.addr_deliv;
    this.order = order;
    this.sales = [];		// #29 : l'ancienne liste ne survit pas à la relecture, l'enregistrement ne retirerait pas un BL de vente qu'elle croit décoché
    this.dead = new Set();
    this.active = pickActive(order, [...this.requested, ...this.collect], deliveryId, this.collect, (r) => this.mine(r));
    const { onBl, toPlan, elsewhere, legacy } = rowsOf(order, this.active, this.collect);
    this.rows = [...onBl, ...toPlan].sort((a, b) => a.seq - b.seq);
    this.elsewhere = elsewhere.filter((r) => this.mine(r));
    this.legacy = legacy;
    this.chosen = new Map(onBl.map((r) => [r.id, r.qty]));
    this.savedOn = new Map(this.active?.id ? onBl.map((r) => [r.id, this.active!]) : []);
    // Sur le BL à créer, tout ce qui reste arrive coché (JP 29/10) ; un BL enregistré reste tel quel, seul ce qui est demandé s'y ajoute (JP 31/10).
    const fresh = !this.active;
    if (!this.locked) for (const r of toPlan) if (((fresh && this.mine(r)) || this.requested.includes(r.id) || this.collect.includes(r.id)) && !closedPose(r)) this.chosen.set(r.id, r.qty);
    // Le pré-cochage du BL à créer n'est pas un changement : changer d'onglet ne l'enregistre pas ; ancienne reprise à retirer : le BL est à enregistrer.
    this.changed = (!fresh && toPlan.some((r) => this.chosen.has(r.id))) || (!this.locked && legacy.length > 0);
    this.formGroup = Delivery.asFormGroup(this.active ?? this.draft());
    this.loadOrderEventsSubject.next(this.formGroup);
    if (!sameAddress) this.geoFor = '';
    this.loadBeneficiary();
    this.initGeo();
  }

  // Commandes de vente du passage : leurs lignes à livrer le rejoignent, sur leur propre BL (un BL n'a qu'une commande).
  private loadSales(then?: () => void): void {
    if (this.locked) { this.sales = []; this.salesLoading = false; then?.(); return; }
    this.subscriptions.add(this.quotationService.getRentalSaleOrders(this.data.id).pipe(catchError((e) => {
      this.alertService.error('ERREUR à la lecture des commandes de vente du dossier : ' + serverMessage(e));
      return of([] as Sales_order[]);
    })).subscribe((orders) => {
      this.applySales(passageSales(orders, Number(this.active?.id) || 0));
      this.salesLoading = false;
      then?.();
    }));
  }

  // Lignes des commandes de vente dans le passage : celles de leur BL rattaché au BL affiché (cochées, sauf vente annulée), celles à livrer de ce patient, cochées si la commande est demandée.
  private applySales(sales: PassageSale[]): void {
    this.sales = sales;
    this.dead = new Set(sales.filter((s) => s.dead).map((s) => s.order.id));
    const fresh: RentalDeliveryRow[] = [];
    for (const s of sales) {
      const { onBl, toPlan } = rowsOf(s.order, s.active);
      if (s.dead) { fresh.push(...onBl); if (s.active?.id && !this.locked) this.changed = true; continue; }	// BL d'une vente annulée : retiré à l'enregistrement du passage
      for (const r of onBl) { this.chosen.set(r.id, r.qty); if (s.active?.id) this.savedOn.set(r.id, s.active); }
      const planned = s.plannable ? toPlan : [];
      fresh.push(...onBl, ...planned.filter((r) => this.mine(r) || this.saleOrderIds.includes(r.orderId)));
      for (const r of planned) if (this.saleOrderIds.includes(r.orderId)) { this.chosen.set(r.id, r.qty); this.changed = true; }
    }
    const own = (r: RentalDeliveryRow) => (r.orderId === this.data.id ? 0 : 1);
    this.rows = [...this.rows.filter((r) => r.orderId === this.data.id), ...fresh].sort((a, b) => own(a) - own(b) || a.orderId - b.orderId || a.seq - b.seq);
  }

  isSale(r: RentalDeliveryRow): boolean { return r.orderId !== this.data.id; }
  isDead(r: RentalDeliveryRow): boolean { return this.dead.has(r.orderId); }

  private draft(): Delivery {
    const d = new Delivery();
    d.id_order = this.data.id;
    d.mode = constants.SALES_ORDER_MODE_DATE_DELIVERY;		// une location se livre : « Livraison programmée », le jour même par défaut
    return d;
  }

  get locked(): boolean { return isSigned(this.active); }
  get openBls(): Delivery[] { return this.order ? openDeliveries(this.order) : []; }
  get blRows(): RentalDeliveryRow[] { return this.rows.filter((r) => this.chosen.has(r.id)); }
  get otherRows(): RentalDeliveryRow[] { return this.locked ? [] : this.rows.filter((r) => !this.chosen.has(r.id) && this.mine(r) && !this.isDead(r)); }
  get deadRows(): RentalDeliveryRow[] { return this.locked ? [] : this.rows.filter((r) => this.isDead(r)); }
  get deadBls(): number[] { return this.sales.flatMap((s) => (s.dead && s.active?.id ? [s.active.id] : [])); }
  private mine(r: RentalDeliveryRow): boolean { return this.patientKey === null || normalizeSearch(r.patient) === this.patientKey; }
  isChosen(r: RentalDeliveryRow): boolean { return this.chosen.has(r.id); }

  // Lignes que la case de l'en-tête coche ou décoche : celles du BL et celles proposées à ce passage.
  private get selectable(): RentalDeliveryRow[] { return this.locked ? [] : this.rows.filter((r) => (this.chosen.has(r.id) || this.mine(r)) && !this.isDead(r) && (this.chosen.has(r.id) || !closedPose(r))); }
  closedPose(r: RentalDeliveryRow): boolean { return closedPose(r); }
  readonly closedLine = CLOSED_LINE;
  get allChosen(): boolean { const s = this.selectable; return s.length > 0 && s.every((r) => this.chosen.has(r.id)); }
  get someChosen(): boolean { return this.selectable.some((r) => this.chosen.has(r.id)); }

  // `box` : la case cliquée, remise dans l'état du modèle tant que le retrait n'est pas confirmé.
  async toggleAll(box?: HTMLInputElement): Promise<void> {
    if (this.locked || this.isSaving) return;
    const rows = this.selectable;
    if (this.allChosen) {
      if (box) box.checked = true;
      if (!(await this.confirmRemoval(rows))) return;
      for (const r of rows) this.chosen.delete(r.id);
    } else for (const r of rows) this.chosen.set(r.id, r.qty);
    this.changed = true;
  }

  async toggle(r: RentalDeliveryRow, box?: HTMLInputElement): Promise<void> {
    if (this.locked || this.isSaving || this.isDead(r)) return;
    if (!this.chosen.has(r.id) && closedPose(r)) { this.alertService.warn(CLOSED_LINE); return; }
    if (this.chosen.has(r.id)) {
      if (box) box.checked = true;
      if (!(await this.confirmRemoval([r]))) return;
      this.chosen.delete(r.id);
    } else this.chosen.set(r.id, r.qty);
    this.changed = true;
  }

  // Jamais de retrait silencieux (JP 31/10) : décocher un matériel déjà écrit sur un BL dit de quel BL et de quel jour il sort ; le reste se décoche sans question.
  private async confirmRemoval(rows: RentalDeliveryRow[]): Promise<boolean> {
    const byBl = new Map<Delivery, RentalDeliveryRow[]>();
    for (const r of rows) { const d = this.savedOn.get(r.id); if (d) byBl.set(d, [...(byBl.get(d) ?? []), r]); }
    if (!byBl.size) return true;
    const n = [...byBl.values()].reduce((t, l) => t + l.length, 0);
    const what = [...byBl].map(([d, l]) => `${l.map((r) => `« ${esc(r.name)} »`).join(', ')} du BL n° ${d.id} ${esc(this.plannedFor(d))}`).join(' ; ');
    const answer = await Swal.fire({
      title: n > 1 ? `Retirer ${n} matériels du BL ?` : 'Retirer du BL ?', icon: 'warning',
      html: `Retirer ${what} ? ${n > 1 ? 'Ils ne seront plus planifiés.' : 'Il ne sera plus planifié.'}<br><small>Le retrait est écrit à l'enregistrement du BL.</small>`,
      showCancelButton: true, focusCancel: true, confirmButtonText: 'Oui, retirer', cancelButtonText: 'Annuler',
      willOpen: (popup) => liftToTopLayer(popup.parentElement),
    });
    return !!answer.isConfirmed;
  }

  private plannedFor(d: Delivery): string {
    const when = blWhen(d, this.deliverySlots);
    return /^\d/.test(when) ? `prévu le ${when}` : `(${when})`;
  }

  // Onglet d'un BL : son jour et son créneau prévus.
  blWhen(d: Delivery): string { return blWhen(d, this.deliverySlots, 'DD/MM'); }
  whenOf(id: number): string { const d = this.openBls.find((x) => x.id === id); return d ? this.blWhen(d) : ''; }

  onPlanificationChanged(): void { this.changed = true; }

  // --- Enregistrer : INSERT du BL à créer, sinon UPDATE ; refus du serveur (garde « un seul BL ouvert ») affiché tel quel ---

  // Lignes cochées d'une commande du passage (le dossier, ou une commande de vente) : un BL ne porte que les lignes de sa commande.
  private chosenOf(orderId: number): Map<number, number> {
    const ids = new Set(this.rows.filter((r) => r.orderId === orderId).map((r) => r.id));
    return new Map([...this.chosen].filter(([id]) => ids.has(id)));
  }

  private payload(raw: any, orderId: number, deliveryId: number): FormGroup {
    const fg = new FormGroup({});
    for (const key of Object.keys(raw)) if (key !== 'lnk_deliveryl') fg.addControl(key, new FormControl(raw[key]));
    fg.addControl('lnk_deliveryl', this.fb.array(deliveryLinesOf(this.chosenOf(orderId), deliveryId).map((l) => new FormGroup({
      id_delivery: new FormControl<number>(l.id_delivery), id_order_l: new FormControl<number>(l.id_order_l), qty: new FormControl<number>(l.qty),
    }))));
    return fg;
  }

  // Le passage : BL du dossier (s'il porte une ligne du dossier), puis BL de chaque commande de vente cochée, même planification ; une vente décochée de tout son BL le retire.
  private async save(then: (written: PassageBl[]) => void): Promise<void> {
    if (this.isSaving || this.locked) return;
    if (this.salesLoading) { this.alertService.warn('Lecture des commandes de vente du passage en cours : un instant'); return; }
    if (!this.chosen.size) { this.alertService.warn(this.active?.id ? 'Aucun matériel sur ce BL : supprimez-le plutôt' : 'Cochez au moins un matériel à livrer ou à reprendre'); return; }
    const mine = this.chosenOf(this.data.id);
    // Un passage part avec un matériel du dossier : un BL de vente seul n'aurait pas de BL de tête et la fenêtre ne le reverrait plus.
    if (!mine.size) { this.alertService.warn(this.active?.id ? 'Aucun matériel du dossier sur ce BL : supprimez-le plutôt' : 'Aucun matériel du dossier coché : une vente seule se livre depuis la fenêtre de livraison de sa commande'); return; }
    const raw = this.formGroup.getRawValue();
    const written: PassageBl[] = [];
    const toValidate = this.sales.filter((s) => s.order.state === SalesOrderState.CREATED && this.chosenOf(s.order.id).size > 0);
    this.isSaving = true;
    try {
      // #27 : une F2 en création se valide ici, juste avant son BL ; même garde de caisse que toute validation.
      if (toValidate.length && !(await periodAllowsSave(this.salesService, this.spin, 'Validation de la commande de vente…'))) { this.isSaving = false; return; }
      for (const s of toValidate) await this.validateSale(s.order.id);
      if (mine.size) written.push(await this.write(this.order!, this.payload(raw, this.data.id, Number(this.active?.id) || 0), 'Enregistrement du BL du dossier…'));
      const head = Number(written.find((w) => w.order.id === this.data.id)?.fg.get('id')?.value) || 0;	// BL du dossier : chaque BL de vente du passage s'y rattache (id_planning)
      for (const s of this.sales) {
        const id = Number(s.active?.id) || 0;
        if (this.chosenOf(s.order.id).size) written.push(await this.write(s.order, this.payload(salePassageDelivery(raw, s.order.id, s.active, head), s.order.id, id), `Enregistrement du BL de la commande ${s.order.id}…`));
        else if (id) await this.remove(s.active!, `Retrait du BL ${id} de la commande ${s.order.id}…`);
      }
    } catch (e: any) {
      this.isSaving = false;
      this.wrote = this.wrote || written.length > 0;
      this.refused(e instanceof Error ? e.message : serverMessage(e));
      // Une F2 validée a changé d'état et d'id de lignes : la fenêtre relit, un second clic n'enverrait pas l'état d'avant.
      if (written.length || toValidate.length) this.load(Number(written.find((w) => w.order.id === this.data.id)?.fg.get('id')?.value) || Number(this.active?.id) || undefined);
      return;
    }
    this.isSaving = false;
    this.wrote = true;
    this.changed = false;
    this.requested = [];
    this.collect = [];
    this.saleOrderIds = [];
    const dossierBl = Number(written.find((w) => w.order.id === this.data.id)?.fg.get('id')?.value) || undefined;
    this.geo.saveIfDirty(this.deliveryOwnerId, this.deliveryAddress, () => this.load(dossierBl, () => then(written)));
  }

  private async write(order: Sales_order, fg: FormGroup, label: string): Promise<PassageBl> {
    const id = Number(fg.get('id')?.value) || 0;
    const response: any = await firstValueFrom(this.logisticService.postDeliveryEvent(id ? constants.EVENTTYP_UPDATE : constants.EVENTTYP_INSERT, fg).pipe(this.spin.block(label)));
    if (response?.success === false) throw new Error(response.msg || response.error_msg || 'refusé');
    const savedId = Number(response?.data?.delivery_id) || id;
    fg.get('id')?.setValue(savedId);
    (fg.get('lnk_deliveryl')?.value ?? []).forEach((_: any, i: number) => fg.get(['lnk_deliveryl', i, 'id_delivery'])?.setValue(savedId));
    return { order, fg };
  }

  // #27 : F2 relue juste avant ; validée seulement si elle est encore en création (déjà validée ailleurs : rien à faire).
  private async validateSale(orderId: number): Promise<void> {
    const fg = await firstValueFrom(this.quotationService.getQuotationAsFormGroup(orderId).pipe(this.spin.block(`Lecture de la commande de vente ${orderId}…`)));
    if (!fg || Number(fg.get('data')?.value?.rental_order_id) !== this.data.id) throw new Error(`commande de vente ${orderId} introuvable pour le dossier ${this.data.id}`);
    const state = fg.get('state')?.value;
    if (state === SalesOrderState.PREPARATION || state === SalesOrderState.DELIVERED) return;
    if (state !== SalesOrderState.CREATED) throw new Error(`commande de vente ${orderId} « ${constants.getOrderLabel(state)} » : elle ne se livre plus`);
    const before = fg.getRawValue().lines ?? [];
    fg.patchValue({ state: SalesOrderState.PREPARATION });
    prepareRentalSave(fg, null, this.loginService.currentstore ?? 0);
    const response: any = await firstValueFrom(this.quotationService.postQuotationEvent(constants.EVENTTYP_UPDATE, fg).pipe(this.spin.block(`Validation de la commande de vente ${orderId}…`)));
    if (!response?.success) throw new Error(`commande de vente ${orderId} non validée : ${response?.msg || response?.error_msg || 'refusée'}`);
    // Cet UPDATE recrée ses lignes sous d'autres id : son BL viserait des lignes effacées (« la ligne N n'existe pas », 460597 le 02/11).
    const after = await firstValueFrom(this.quotationService.getQuotationAsFormGroup(orderId).pipe(this.spin.block(`Relecture de la commande de vente ${orderId}…`)));
    this.renumber(orderId, renumberedLines(before, after?.getRawValue().lines ?? []));
  }

  // Lignes d'une commande passées sur leurs id relus ; une ligne cochée sans id neuf arrête l'enregistrement avant son BL.
  private renumber(orderId: number, ids: ReadonlyMap<number, number>): void {
    const own = new Set(this.rows.filter((r) => r.orderId === orderId).map((r) => r.id));
    const lost = this.rows.filter((r) => r.orderId === orderId && this.chosen.has(r.id) && !ids.has(r.id));
    if (lost.length) throw new Error(`commande de vente ${orderId} relue après sa validation, ${lost.map((r) => r.name).join(', ')} n'y est plus : rien n'est planifié pour elle`);
    const to = (id: number) => (own.has(id) ? ids.get(id) ?? id : id);
    this.rows = this.rows.map((r) => (r.orderId === orderId ? { ...r, id: to(r.id) } : r));
    this.chosen = new Map([...this.chosen].map(([id, qty]) => [to(id), qty]));
    this.savedOn = new Map([...this.savedOn].map(([id, d]) => [to(id), d]));
  }

  private async remove(d: Delivery, label: string): Promise<void> {
    const response: any = await firstValueFrom(this.logisticService.postDeliveryEvent(constants.EVENTTYP_DELETE, Delivery.asFormGroup(d)).pipe(this.spin.block(label)));
    if (response?.success === false) throw new Error(response.msg || response.error_msg || 'refusé');
  }

  private refused(msg: string): void {
    const text = (msg || 'ERREUR Serveur').replace(/^Process Error:\s*/, '');
    this.alertService.error(text.startsWith('Bon de livraison refusé') ? text : "Le BL n'a pas été enregistré : " + text);
  }

  onPlan(): void {
    void this.save((w) => this.announcePlanned(w[0].fg));
  }

  private announcePlanned(fg: FormGroup): void {
    this.alertService.success('La planification a bien eu lieu', { action: this.planningLink(fg.getRawValue()) });
  }

  // Lien de l'en-tête : celui de la snack bar, sur le BL tel qu'enregistré (une date changée mais pas encore enregistrée ne le déplace pas).
  get savedPlanningLink(): AlertAction | undefined { return this.planningLink(this.active); }

  // « Voir au planning », même règle que la fenêtre F2 : tournées du jour demandé, sur sa demi-journée, BL en surbrillance (?bl=) ; nouvel onglet puis fermeture.
  planningLink(d: Pick<Delivery, 'id' | 'mode' | 'askdatdel' | 'askslot'> | null): AlertAction | undefined {
    const id = Number(d?.id) || 0;
    const dat = d?.askdatdel;		// 'YYYY-MM-DD' : normalisée par beforeSubmit à l'envoi, rendue ainsi par le serveur (NaiveDate)
    if (!d || (Number(d.mode) || 0) < constants.SALES_ORDER_MODE_ASAP_DELIVERY || !id || !dat) return undefined;
    const day = DelrunsService.slotDayOf(d.askslot, this.deliverySlots);
    const seg = ['/logistic/delruns', dat, ...(day === 1 ? ['matin'] : day === 2 ? ['apres-midi'] : [])];
    const url = this.router.serializeUrl(this.router.createUrlTree(seg, { queryParams: { bl: id } }));
    return { label: 'Voir au planning', run: () => {
      if (!window.open(url, '_blank')) { this.alertService.error('Onglet bloqué par le navigateur — autorisez les pop-ups pour ce site'); return; }
      this.close();
    } };
  }

  // Un BL imprimé par commande du passage : celui du dossier, puis ceux des commandes de vente.
  onPrint(): void {
    if (this.locked) { this.printSigned(); return; }
    void this.save(async (written) => {
      for (const { fg } of written) {
        this.logisticService.beforeSubmit(fg);
        try {
          const response: any = await firstValueFrom(this.logisticService.postDeliveryPrintEvent$(fg).pipe(this.spin.block('Génération du bon de livraison…')));
          if (!this.printer.printRendered(response?.data)) { this.alertService.error('ERREUR lors de la génération du BL ' + fg.get('id')?.value); return; }
        } catch (e) { this.refused(serverMessage(e)); return; }
      }
      this.announcePlanned(written[0].fg);
    });
  }

  private printSigned(): void {
    const id = Number(this.active?.id) || 0;
    if (!id || this.isSaving) return;
    this.isSaving = true;
    this.subscriptions.add(this.logisticService.getSignedBl$(id).pipe(this.spin.block('Bon de livraison signé…')).subscribe({
      next: (blob: Blob) => { this.isSaving = false; this.printer.printPdfBlob(blob); },
      error: (e) => { this.isSaving = false; this.alertService.error(e?.status === 404 ? 'Aucun BL signé pour cette livraison' : 'ERREUR lors de la génération du BL'); },
    }));
  }

  // Matériel remis ou rendu au magasin : le BL est enregistré, puis signé tout de suite (même dialogue de signature que la fenêtre F2).
  async onSignNow(): Promise<void> {
    if (this.locked || this.isSaving) return;
    const answer = await Swal.fire({
      title: 'Signer maintenant', icon: 'warning', showCancelButton: true, focusCancel: true, cancelButtonText: 'Annuler',
      confirmButtonText: 'Oui, le client signe maintenant au magasin',
      html: 'Ce bouton <b>ne programme aucune livraison</b> : le client emporte ou rapporte <b>maintenant</b> au magasin le matériel de ce BL, puis il signe le bon.<br><br>'
        + 'Pour une livraison ou une reprise chez le client, cliquez sur «&nbsp;Annuler&nbsp;» puis sur «&nbsp;Planifier&nbsp;».',
      willOpen: (popup) => liftToTopLayer(popup.parentElement),
    });
    if (!answer.isConfirmed) return;
    // Une signature pour tout le passage (JP 31/10), posée sur chaque BL : ventes d'abord, BL du dossier en dernier (le serveur refuse le dossier avant ses ventes ouvertes).
    void this.save(async (written) => {
      const head = written.find((w) => w.order.id === this.data.id) ?? written[0];
      const notes = written.map(({ order, fg }) => {
        const qties = new Map(fg.getRawValue().lnk_deliveryl.map((l: any) => [Number(l.id_order_l), { undelivered_qty: 0, qties: [Number(l.qty)] }]));
        return DeliveryComponent.buildDeliveryNoteLines(order.lines ?? [], qties as any, 0);
      });
      const total = (k: 'amount_taxi' | 'amount_taxx') => roundHalfAway(notes.reduce((t, n) => t + (Number(n[k]) || 0), 0), 2);
      const sig: { signature: string; note: string } | null = await firstValueFrom(this.dialog.open(DeliveryValidationComponent, {
        width: '850px', maxWidth: '95vw',
        data: { order: this.order, deliveryFormGroup: head.fg, customer: this.order?.lnk_customer, lines: notes.flatMap((n) => n.lines), amount_taxi: total('amount_taxi'), amount_taxx: total('amount_taxx'), collectOnly: true },
      }).afterClosed());
      const signed: number[] = [];
      for (const { fg } of sig?.signature ? [...written.filter((w) => w !== head), head] : []) {
        const id = Number(fg.get('id')?.value) || 0;
        this.logisticService.beforeSubmit(fg);
        try {
          const response: any = await firstValueFrom(this.logisticService.postDeliveryValidation(fg, sig!.signature, sig!.note).pipe(this.spin.block(`Signature du BL ${id}…`)));
          if (response?.success === false) throw new Error(response.msg || 'refusée');
          signed.push(id);
        } catch (e: any) {
          const text = (e instanceof Error ? e.message : serverMessage(e)).replace(/^Process Error:\s*/, '');
          this.alertService.error(`Signature du BL ${id} refusée : ${text}` + (signed.length ? ` (BL ${signed.join(', ')} signé${signed.length > 1 ? 's' : ''})` : ''));
          break;
        }
      }
      if (signed.length === written.length) this.alertService.success(`${signed.length} BL signé${signed.length > 1 ? 's' : ''} — dossier ${this.data.id}`);
      this.load(Number(head.fg.get('id')?.value) || undefined);
    });
  }

  async onDelete(): Promise<void> {
    const d = this.active;
    if (!d?.id || this.locked || this.isSaving || this.salesLoading) return;
    const saleBls = this.sales.flatMap((s) => (s.active?.id ? [s.active] : []));
    const answer = await Swal.fire({
      title: `Supprimer le BL ${d.id} ?`, icon: 'warning',
      text: 'Le matériel de ce BL redevient à planifier ; sa planification est perdue.' + (saleBls.length ? ` Le BL ${saleBls.map((b) => b.id).join(', ')} des ventes de ce passage est supprimé aussi.` : ''),
      showCancelButton: true, focusCancel: true, confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler',
      willOpen: (popup) => liftToTopLayer(popup.parentElement),
    });
    if (!answer.isConfirmed) return;
    this.isSaving = true;
    try {
      for (const b of [...saleBls, d]) await this.remove(b, `Suppression du BL ${b.id}…`);	// ventes d'abord : un échec ne laisse jamais un BL de vente sans son BL de tête
    } catch (e: any) {
      this.refused(e instanceof Error ? e.message : serverMessage(e));
    }
    this.isSaving = false;
    this.wrote = true;
    this.load();
  }

  // « Livré le… » (JP 31/10) : le BL enregistré est déclaré livré au jour choisi (703, comme « Passer à livré » du Suivi des livraisons), qui pose realdatdel.
  // Mêmes avertissements que le Suivi ; les changements non enregistrés du BL partent d'abord.
  async onDelivered(): Promise<void> {
    const d = this.active;
    if (!d?.id || this.locked || this.isSaving || this.salesLoading) return;
    const saleBls = this.sales.flatMap((s) => (s.active?.id && !s.dead ? [s.active.id] : []));
    const proof = (d.lnk_attachs ?? []).some((a) => Array.isArray(a?.data?.['flags']) && a.data!['flags'].includes(ATTACH_FLAG_SCAN_BL));
    const day = await askDate(this.dialog, {
      title: 'Déclarer livré ?',
      html: `Le BL <b>${d.id}</b> ${esc(this.plannedFor(d))} sera déclaré livré le jour choisi. Un BL livré ne se modifie plus.`
        + (this.changed ? '<br><br>Les changements de ce BL sont enregistrés d\'abord.' : '')
        + (saleBls.length ? `<br><br>Location : le passage est livré en entier, BL du dossier et BL ${saleBls.join(', ')} des ventes ensemble.` : '')
        + (proof ? '' : '<br><br>⚠ Ce BL n\'a ni scan du BL papier ni BL signé à l\'écran.')
        + '<br><small>Un jour à venir est daté de maintenant.</small>',
      warn: !proof,
      label: 'Jour de livraison',
      day: dayjs(businessDayOf(d.askdatdel) ?? todayStr()).toDate(),
      confirm: 'Déclarer livré',
    });
    if (!day) return;
    if (this.changed) { void this.save((written) => void this.deliver(Number(written.find((w) => w.order.id === this.data.id)?.fg.get('id')?.value) || d.id, day)); return; }
    await this.deliver(d.id, day);
  }

  private async deliver(id: number, day: Date): Promise<void> {
    this.isSaving = true;
    try {
      const response: any = await firstValueFrom(this.tourReturnService.postDeliveryReturn$([id], day).pipe(this.spin.block('Passage du BL à « livré »…')));
      if (response?.success === false) throw new Error(response.msg || response.error_msg || 'refusé');
      const done: number[] = response?.data?.done ?? [];
      const skipped: number[] = response?.data?.skipped ?? [];
      const label = dayjs(day).format('DD/MM/YYYY');
      if (done.length) this.alertService.success(`${done.length} BL déclaré${done.length > 1 ? 's' : ''} livré${done.length > 1 ? 's' : ''} le ${label} (BL ${done.join(', ')})`
        + (skipped.length ? ` — non passé${skipped.length > 1 ? 's' : ''} (déjà livré ou commande annulée) : ${skipped.join(', ')}` : ''));
      else this.alertService.warn(`BL ${id} non passé à « livré » (déjà livré ou commande annulée)`);
      this.wrote = true;
    } catch (e: any) {
      this.alertService.error(`Le BL ${id} n'a pas été déclaré livré : ` + (e instanceof Error ? e.message : serverMessage(e)).replace(/^Process Error:\s*/, ''));
    }
    this.isSaving = false;
    this.load(id);
  }

  // Autre BL ouvert du dossier : les changements du BL affiché sont enregistrés d'abord, comme l'onglet de la fenêtre F2.
  selectBl(d: Delivery): void {
    if (d.id === this.active?.id || this.isSaving) return;
    if (this.changed && !this.locked && this.chosen.size) { void this.save(() => this.load(d.id)); return; }
    this.load(d.id);
  }

  // Lien d'un matériel « Déjà sur un autre BL ouvert » : l'onglet de ce BL.
  goToBl(id: number): void {
    const d = this.openBls.find((x) => x.id === id);
    if (d) this.selectBl(d);
  }

  // BL de plus pour ce client (autre jour, autre tournée) : seuls les matériels qui ne sont sur aucun BL ouvert peuvent y aller ; le BL affiché est enregistré d'abord, comme pour un onglet.
  newBl(): void {
    if (this.isSaving || this.salesLoading || !this.order || !this.active) return;
    if (this.changed && !this.locked && this.chosen.size) { void this.save(() => this.newBl()); return; }
    const unread = this.locked;	// BL signé affiché : loadSales n'a pas lu les ventes du dossier, elles le sont pour le BL neuf
    this.active = null;
    this.requested = [];
    this.changed = false;
    this.savedOn = new Map();
    const { toPlan, elsewhere } = rowsOf(this.order, null, this.collect);
    this.rows = toPlan;
    this.elsewhere = elsewhere.filter((r) => this.mine(r));
    this.legacy = [];
    this.chosen = new Map(toPlan.filter((r) => this.mine(r) && !closedPose(r)).map((r) => [r.id, r.qty]));
    if (unread) { this.salesLoading = true; this.loadSales(); }
    else this.applySales(this.sales.filter((x) => x.plannable).map((x) => ({ ...x, active: null })));	// autre passage : les restes des ventes qui se livrent encore
    this.formGroup = Delivery.asFormGroup(this.draft());
    this.loadOrderEventsSubject.next(this.formGroup);
  }

  close(): void {
    this.dialogRef.close({ changed: this.wrote });
  }

  // --- En-tête : client, bénéficiaire, adresse ---

  get customerLabel(): string {
    const c = this.order?.lnk_customer;
    const label = [c?.firstname, c?.name].filter((x) => !!x).join(' ');
    return label || 'ERREUR : Client non renseigné';
  }

  get isDistinctBeneficiary(): boolean {
    const recipientId = this.order?.data?.recipient;
    return !!recipientId && +recipientId !== +(this.order?.customer ?? 0);
  }

  get beneficiaryLabel(): string {
    const b = this.beneficiary ?? this.order?.lnk_partner_deliv;
    return [b?.firstname, b?.name].map((x: any) => (x ?? '').trim()).filter((x: string) => !!x).join(' ');
  }

  // Nom du bénéficiaire ou du payeur : sa fiche tiers par-dessus la fenêtre ; au retour, le nom relu remplace l'ancien.
  openParty(id: number | null | undefined): void {
    if (!id) return;
    this.subscriptions.add(openPartnerSheet(this.dialog, +id).subscribe((back) => {
      if (!back?.id) return;
      for (const x of [this.beneficiary, this.order?.lnk_customer, this.order?.lnk_partner_deliv]) {
        if (x && +x.id === +back.id) Object.assign(x, { name: back.name, firstname: back.firstname });
      }
    }));
  }

  // Fiche du bénéficiaire relue une fois par dossier : l'adresse de livraison est la sienne, pas celle du payeur.
  private loadBeneficiary(): void {
    if (!this.isDistinctBeneficiary) { this.beneficiary = null; return; }
    const id = Number(this.order!.data.recipient);
    if (Number(this.beneficiary?.id) === id) return;
    this.subscriptions.add(this.partnerService.getPartnerByid(id).subscribe({
      next: (p: any) => { this.beneficiary = Array.isArray(p) ? p[0] : p; this.initGeo(); },
      error: (e: any) => console.error('[RentalDeliveryDialog] tiers bénéficiaire', e),
    }));
  }

  // Carte : une fois par adresse de livraison connue (celle du bénéficiaire attend sa fiche).
  private geoFor = '';
  private initGeo(): void {
    if (!this.order || (this.isDistinctBeneficiary && !this.beneficiary)) return;
    const key = `${this.deliveryOwnerId}:${this.order.addr_deliv}`;
    if (key === this.geoFor) return;
    this.geoFor = key;
    this.geo.init(this.deliveryAddress, this.deliveryName, () => this.deliveryAddress);
  }

  // Propriétaire de l'adresse de livraison : le bénéficiaire quand il n'est pas le client (HAD, tiers payeur), sinon le client.
  get deliveryOwnerId(): number {
    return Number(this.isDistinctBeneficiary ? this.order?.data?.recipient : this.order?.customer) || 0;
  }

  get deliveryName(): string {
    return this.isDistinctBeneficiary ? (this.beneficiaryLabel || '…') : this.customerLabel;
  }

  get deliveryAddress(): any {
    const owner = this.isDistinctBeneficiary ? this.beneficiary : this.order?.lnk_customer;
    return Partner.addresses(owner).find((a: any) => a.id == this.order?.addr_deliv) ?? null;
  }

  get addressLabel(): string {
    const owner = this.isDistinctBeneficiary ? this.beneficiary : this.order?.lnk_customer;
    if (!owner) return this.isDistinctBeneficiary ? '…' : '';
    if (!Partner.addresses(owner).length) return 'Non renseignée';
    const a = this.deliveryAddress;
    return a ? addressText(a) : 'ERREUR : Adresse de livraison non renseignée';
  }

  get billAddressLabel(): string {
    const a = Partner.addresses(this.order?.lnk_customer).find((x: any) => x.id == this.order?.addr_bill);
    return a ? addressText(a) : 'Adresse de facturation non renseignée';
  }

  onMapCoords(c: { lat: number; lon: number }): void { this.geo.moved(c); }
}

const addressText = (a: any): string => [a.street1, a.street3, a.street2, a.zip, a.city].filter((v: any) => !!v).join(' ');

// BL écrit par le passage, avec sa commande (le dossier ou une commande de vente) : impression et signature BL par BL.
interface PassageBl { order: Sales_order; fg: FormGroup; }

// La fenêtre (MatDialog, Angular 22) vit dans le top layer : sans cela la Swal est peinte dessous (même procédé que la fenêtre F2).
function liftToTopLayer(container: HTMLElement | null): void {
  if (!container || typeof container.showPopover !== 'function') return;
  container.setAttribute('popover', 'manual');
  Object.assign(container.style, { width: 'auto', margin: '0', border: '0', color: 'inherit' });
  try { container.showPopover(); } catch (e) { console.warn('[RentalDeliveryDialog] showPopover refusé', e); }
}

function esc(v: string): string {
  return v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
